// DocumentsManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Hoot from "../../Hoot";
import { toast } from "react-toastify";

import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button, Form, Spinner } from "react-bootstrap";

import { Avatar } from "@mui/material";
import {
  FiFileText,
  FiRefreshCw,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiSearch,
  FiDownload,
  FiEye,
  FiUpload,
  FiX,
  FiAlertTriangle,
} from "react-icons/fi";

const DOC_CATEGORIES = [
  { value: "documento_interno", label: "Documento interno" },
  { value: "contrato", label: "Contrato" },
  { value: "proposta", label: "Proposta" },
  { value: "financeiro", label: "Financeiro" },
  { value: "outro", label: "Outro" },
];

const FILE_FIELD = "files";

const normalizeStr = (str) =>
  str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";

const safeValue = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

function resolveUrl(maybeUrlOrPath) {
  if (!maybeUrlOrPath) return null;
  const s = String(maybeUrlOrPath);
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return `${Hoot().slice(0, -1)}${s}`;
  return s;
}

function formatBytes(bytes) {
  const b = Number(bytes || 0);
  if (!b) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let val = b;
  while (val >= 1024 && i < units.length - 1) {
    val /= 1024;
    i++;
  }
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fileIconFor(mimeOrName = "") {
  const s = String(mimeOrName).toLowerCase();
  if (s.includes("pdf") || s.endsWith(".pdf")) return "bi bi-filetype-pdf";
  if (s.includes("image") || s.match(/\.(png|jpg|jpeg|webp|gif)$/)) return "bi bi-file-earmark-image";
  if (s.includes("word") || s.match(/\.(doc|docx)$/)) return "bi bi-filetype-doc";
  if (s.includes("excel") || s.match(/\.(xls|xlsx|csv)$/)) return "bi bi-filetype-xls";
  return "bi bi-file-earmark-text";
}

function isPreviewable(file = {}) {
  const url = resolveUrl(file.url || file.path || file.download_url);
  if (!url) return false;
  const name = (file.name || "").toLowerCase();
  const mime = (file.mime || file.mimetype || "").toLowerCase();

  if (mime.startsWith("image/")) return true;
  if (mime.includes("pdf")) return true;
  if (name.match(/\.(png|jpg|jpeg|webp|gif)$/)) return true;
  if (name.endsWith(".pdf")) return true;

  return false;
}

function isSameOrigin(url) {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

async function fetchAsBlob(url) {
  const res = await axios.get(url, {
    responseType: "blob",
    withCredentials: true,
  });

  const ct = (res.headers?.["content-type"] || "").toLowerCase();
  if (ct.includes("text/html")) {
    throw new Error("Resposta HTML (provável sessão expirada/sem permissão).");
  }
  return res.data;
}

function openBlobInNewTab(blob) {
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

const styles = `/* (MESMO CSS DO TEU) */
:root{
  --primary:#5b5cff;
  --primary2:#7c3aed;
  --bg:#f4f6fb;
  --card:#ffffff;
  --border:#e6eaf2;
  --text:#0f172a;
  --muted:#64748b;
  --shadow: 0 14px 40px rgba(15,23,42,.08);
}
.docs-shell{ background:var(--bg); padding:10px; }
.docs-card{ background:var(--card); border-radius:18px; border:1px solid var(--border); padding:12px 14px 14px; box-shadow: var(--shadow); }
.docs-header{ display:flex; flex-wrap:wrap; justify-content:space-between; align-items:flex-start; gap:10px; margin-bottom:10px; }
.docs-header-title{ font-weight:700; font-size:1.02rem; display:flex; align-items:center; gap:.55rem; color:var(--text); }
.docs-chip{ display:inline-flex; align-items:center; gap:.35rem; padding:.28rem .62rem; border-radius:999px; background:linear-gradient(135deg, rgba(91,92,255,.14), rgba(124,58,237,.12)); color:#4338ca; font-size:.78rem; font-weight:600; border:1px solid rgba(91,92,255,.16); }
.docs-toolbar{ margin-top:6px; padding:10px 10px; border-radius:14px; background:linear-gradient(180deg, rgba(148,163,184,0.12), rgba(148,163,184,0.06)); border:1px solid rgba(226,232,240,.7); margin-bottom:10px; }
.small-muted{ font-size:.78rem; color:var(--muted); }
.docs-table{ width:100%; border-collapse:separate; border-spacing:0; font-size:.86rem; color:var(--text); }
.docs-table thead th{ text-align:left; padding:.62rem .65rem; font-size:.78rem; font-weight:650; color:var(--muted); border-bottom:1px solid var(--border); background:var(--card); position:sticky; top:0; z-index:1; }
.docs-table tbody td{ padding:.62rem .65rem; border-bottom:1px solid var(--border); vertical-align:middle; }
.docs-table tbody tr:hover{ background:rgba(148,163,184,0.10); }
.pill{ display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; font-size:.74rem; font-weight:600; border:1px solid rgba(15,23,42,.08); background:rgba(226,232,240,.55); color:#0f172a; }
.btn-icon{ width:34px; height:34px; border-radius:12px; border:1px solid rgba(226,232,240,.95); background:#fff; display:inline-grid; place-items:center; cursor:pointer; transition: transform .08s ease, background .12s ease; }
.btn-icon:hover{ background: rgba(91,92,255,.06); }
.btn-icon:active{ transform: scale(.98); }
.btn-icon svg{ font-size:1.05rem; color:#334155; }
.actions-row{ display:flex; justify-content:flex-end; gap:8px; flex-wrap:wrap; }
.pretty-modal .modal-dialog{ max-width: 820px; }
@media (max-width: 768px){ .pretty-modal .modal-dialog{ max-width: 96vw; } }
.pretty-modal .modal-content{ border-radius: 18px; border: 1px solid rgba(226,232,240,.95); box-shadow: 0 30px 90px rgba(2,6,23,.22); overflow: hidden; }
.pretty-modal .modal-header{ border-bottom: 1px solid rgba(226,232,240,.9); background: radial-gradient(1000px 260px at 20% 0%, rgba(91,92,255,.16), transparent 60%), radial-gradient(900px 240px at 95% 0%, rgba(124,58,237,.12), transparent 55%), #ffffff; padding: 14px 16px; }
.pretty-modal .modal-body{ padding: 14px 16px 12px; background: linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%); }
.body-scroll{  overflow-y: auto; padding-right: 4px; }
.pretty-modal .modal-footer{ border-top: 1px solid rgba(226,232,240,.9); padding: 12px 16px; background: #ffffff; }
.header-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.header-left{ display:flex; align-items:center; gap:12px; }
.icon-wrap{ width: 44px; height: 44px; border-radius: 14px; background: rgba(91,92,255,.10); border: 1px solid rgba(91,92,255,.18); display:grid; place-items:center; }
.title-sub{ margin-top:2px; font-size: .8rem; color: #64748b; font-weight: 500; }
.form-grid{ display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 12px; }
@media (max-width: 720px){ .form-grid{ grid-template-columns: 1fr; } }
.cardlet{ background: rgba(255,255,255,.9); border: 1px solid rgba(226,232,240,.95); border-radius: 16px; padding: 10px 12px; }
.cardlet-title{ font-size: .78rem; color:#64748b; font-weight: 600; display:flex; align-items:center; gap:8px; margin-bottom: 8px; }
.req{ color:#ef4444; font-weight:700; }
.form-label{ font-size: .78rem !important; color: #475569 !important; font-weight: 600 !important; margin-bottom: 6px !important; }
.form-control, .form-select{ border-radius: 12px !important; border-color: rgba(226,232,240,.95) !important; padding-top: .55rem !important; padding-bottom: .55rem !important; }
.form-control:focus, .form-select:focus{ box-shadow: 0 0 0 .2rem rgba(91,92,255,.15) !important; border-color: rgba(91,92,255,.45) !important; }
.helper{ font-size: .74rem; color:#64748b; margin-top: 6px; }
.file-list{ border:1px solid rgba(226,232,240,.95); border-radius:16px; overflow:hidden; background:#fff; }
.file-item{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:10px 12px; border-bottom:1px solid rgba(226,232,240,.9); }
.file-item:last-child{ border-bottom:none; }
.file-left{ display:flex; align-items:center; gap:10px; min-width:0; }
.file-name{ font-weight:650; color:var(--text); font-size:.86rem; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width: 360px; }
.file-meta{ font-size:.74rem; color:var(--muted); margin-top:2px; }
.file-actions{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }

/* small confirm modal */
.confirm-modal .modal-dialog{ max-width: 420px; }
.confirm-modal .modal-content{ border-radius: 18px; border: 1px solid rgba(226,232,240,.95); box-shadow: 0 30px 90px rgba(2,6,23,.22); overflow:hidden; }
.confirm-modal .modal-header{ border-bottom: 1px solid rgba(226,232,240,.9); padding: 12px 14px; background: radial-gradient(800px 220px at 20% 0%, rgba(239,68,68,.10), transparent 60%), radial-gradient(700px 200px at 95% 0%, rgba(91,92,255,.10), transparent 55%), #ffffff; }
.confirm-modal .modal-body{ padding: 12px 14px 6px; background: linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%); }
.confirm-modal .modal-footer{ border-top: 1px solid rgba(226,232,240,.9); padding: 10px 14px; background:#fff; }
.confirm-title{ display:flex; align-items:center; gap:10px; font-weight: 750; color:#0f172a; }
.confirm-sub{ color:#64748b; font-size:.84rem; margin-top:6px; }
.confirm-icon{ width:42px;height:42px;border-radius:14px; display:grid;place-items:center; background: rgba(239,68,68,.10); border:1px solid rgba(239,68,68,.18); }
`;

export default function DocumentsManager() {
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    title: "",
    category: DOC_CATEGORIES[0].value,
    note: "",
  });

  const [files, setFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [openDetails, setOpenDetails] = useState(false);
  const [detailsRow, setDetailsRow] = useState(null);

  // confirm delete modal
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      setLoadingList(true);
      setError(null);
      const res = await axios.get(Hoot() + "docs/list", { withCredentials: true });
      setRows(res.data || []);
    } catch {
      setError("Erro ao listar documentos.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = normalizeStr(q);
    return (rows || []).filter((r) => {
      const hay = [r.title, r.category, r.note, r.operator].map(normalizeStr).join(" ");
      return hay.includes(s);
    });
  }, [rows, q]);

  const openCreate = () => {
    setEditing(null);
    setError(null);
    setForm({ title: "", category: DOC_CATEGORIES[0].value, note: "" });
    setFiles([]);
    setOpenForm(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setError(null);
    setForm({
      title: row?.title || "",
      category: row?.category || DOC_CATEGORIES[0].value,
      note: row?.note || "",
    });
    setFiles([]);
    setOpenForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setOpenForm(false);
    setEditing(null);
    setError(null);
    setFiles([]);
  };

  const openDetailsModal = async (row) => {
    try {
      setError(null);
      let full = row;

      if (!row?.files) {
        try {
          const res = await axios.get(Hoot() + `docs/byid/${row.id}`, { withCredentials: true });
          full = res.data?.data || res.data || row;
        } catch {}
      }

      setDetailsRow(full);
      setOpenDetails(true);
    } catch {
      setError("Erro ao abrir detalhes do documento.");
    }
  };

  const closeDetails = () => {
    setOpenDetails(false);
    setDetailsRow(null);
  };

  const doUploadOrUpdate = async () => {
    if (!form.title.trim() || !form.category) {
      setError("Preencha título e categoria.");
      return;
    }

    if (!editing?.id && files.length === 0) {
      setError("Selecione pelo menos um ficheiro.");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const hasFiles = files.length > 0;
      if (hasFiles) {
        const fd = new FormData();
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("note", form.note);
        for (const f of files) fd.append(FILE_FIELD, f);

        if (editing?.id) {
          await axios.put(Hoot() + `docs/update/${editing.id}`, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Documento atualizado com sucesso!");
        } else {
          await axios.post(Hoot() + "docs/upload", fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Documento registado com sucesso!");
        }
      } else {
        await axios.put(
          Hoot() + `docs/update/${editing.id}`,
          { title: form.title, category: form.category, note: form.note },
          { withCredentials: true }
        );
        toast.success("Documento atualizado com sucesso!");
      }

      closeForm();
      await load();
    } catch {
      setError("Erro ao guardar documento.");
    } finally {
      setSaving(false);
    }
  };

  // delete confirm
  const askDeleteDoc = (row) => {
    setDeleteTarget(row);
    setOpenConfirmDelete(true);
  };

  const closeConfirmDelete = () => {
    if (saving) return;
    setOpenConfirmDelete(false);
    setDeleteTarget(null);
  };

  const confirmDeleteDoc = async () => {
    const row = deleteTarget;
    if (!row?.id) return;

    try {
      setSaving(true);
      setError(null);
      await axios.delete(Hoot() + `docs/delete/${row.id}`, { withCredentials: true });
      toast.success("Documento removido.");
      closeConfirmDelete();
      await load();
    } catch {
      setError("Erro ao remover documento.");
      toast.error("Erro ao remover documento.");
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (file) => {
    const url = resolveUrl(file?.url || file?.path || file?.download_url);
    if (!url) return toast.error("URL do ficheiro não disponível.");

    try {
      // se for mesmo domínio da API -> blob com credenciais (evita HTML/redirect)
      if (isSameOrigin(url)) {
        const blob = await fetchAsBlob(url);
        openBlobInNewTab(blob);
        return;
      }
      // ImageKit (cross-origin) normalmente abre direto
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(`Erro ao baixar: ${e?.message || "não foi possível abrir o documento"}`);
    }
  };

  const preview = async (file) => {
    const url = resolveUrl(file?.url || file?.path || file?.download_url);
    if (!url) return toast.error("URL do ficheiro não disponível.");

    try {
      if (isSameOrigin(url)) {
        const blob = await fetchAsBlob(url);
        const blobUrl = URL.createObjectURL(blob);
        setPreviewFile({
          url: blobUrl,
          name: file?.name || "Ficheiro",
          mime: file?.mime || file?.mimetype || blob.type || "",
          __blobUrl: true,
        });
        setShowPreview(true);
        return;
      }

      setPreviewFile({
        url,
        name: file?.name || "Ficheiro",
        mime: file?.mime || file?.mimetype || "",
        __blobUrl: false,
      });
      setShowPreview(true);
    } catch (e) {
      toast.error(`Erro no preview: ${e?.message || "não foi possível carregar"}`);
    }
  };

  const closePreview = () => {
    if (previewFile?.__blobUrl && previewFile?.url) {
      try {
        URL.revokeObjectURL(previewFile.url);
      } catch {}
    }
    setShowPreview(false);
    setPreviewFile(null);
  };

  const currentCategoryLabel = (val) => DOC_CATEGORIES.find((c) => c.value === val)?.label || val;

  return (
    <>
      <style>{styles}</style>

      <div className="docs-shell">
        <div className="docs-card">
          <div className="docs-header">
            <div className="docs-header-title">
              <FiFileText style={{ color: "var(--primary)", fontSize: 20 }} />
              <span>Documentos</span>
              <span className="docs-chip">{filtered.length} documentos</span>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <button
                className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center"
                onClick={load}
                disabled={loadingList}
              >
                <FiRefreshCw className="me-2" />
                {loadingList ? "A carregar..." : "Recarregar"}
              </button>

              <button
                className="btn btn-outline-primary btn-sm rounded-pill d-flex align-items-center"
                onClick={openCreate}
                disabled={saving}
              >
                <FiPlus className="me-2" />
                Registar
              </button>
            </div>
          </div>

          {error ? (
            <div className="alert alert-danger py-2 mb-2" style={{ borderRadius: 14 }}>
              {error}
            </div>
          ) : null}

          <div className="docs-toolbar">
            <div className="row g-2 align-items-center">
              <div className="col-12">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <FiSearch />
                  </span>
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Pesquisar por título, categoria, nota, operador..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Título</th>
                  <th>Categoria</th>
                  <th>Ficheiros</th>
                  <th>Operador</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td>{safeValue(r.date)}</td>
                    <td style={{ fontWeight: 650 }}>{safeValue(r.title, "Sem título")}</td>
                    <td>
                      <span className="pill">
                        <i className="bi bi-tag" />
                        {currentCategoryLabel(r.category)}
                      </span>
                    </td>
                    <td>
                      <span className="pill">
                        <i className="bi bi-paperclip" />
                        {r.files_count ?? (r.files?.length ?? 0)}
                      </span>
                    </td>
                    <td>{safeValue(r.operator)}</td>

                    <td className="text-end">
                      <div className="actions-row">
                        <button className="btn-icon" title="Ver ficheiros" onClick={() => openDetailsModal(r)}>
                          <FiEye />
                        </button>

                        <button className="btn-icon" title="Editar" onClick={() => openEdit(r)} disabled={saving}>
                          <FiEdit3 />
                        </button>

                        <button className="btn-icon" title="Remover" onClick={() => askDeleteDoc(r)} disabled={saving}>
                          <FiTrash2 style={{ color: "#ef4444" }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <span className="small-muted">Sem documentos.</span>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL FORM */}
      <Modal show={openForm} onHide={closeForm} centered backdrop="static" keyboard={!saving} className="pretty-modal">
        <Modal.Header closeButton={!saving}>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiFileText style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>{editing ? "Atualizar documento" : "Registar documento"}</div>
                <div className="title-sub">Preenche os campos essenciais e anexa os ficheiros.</div>
              </div>
            </div>

            {files?.[0] ? (
              <Avatar variant="rounded" sx={{ width: 44, height: 44, borderRadius: 3 }}>
                <FiFileText />
              </Avatar>
            ) : null}
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {error ? (
              <div className="alert alert-danger py-2 mb-3" style={{ borderRadius: 14 }}>
                {error}
              </div>
            ) : null}

            <Form>
              <div className="form-grid">
                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <i className="bi bi-card-text" />
                    Identificação
                  </div>

                  <div className="form-grid">
                    <Form.Group>
                      <Form.Label>
                        Título <span className="req">*</span>
                      </Form.Label>
                      <Form.Control
                        value={form.title}
                        onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                        placeholder="Ex.: Contrato EDUALL"
                        required
                      />
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>
                        Categoria <span className="req">*</span>
                      </Form.Label>
                      <Form.Select
                        value={form.category}
                        onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                        required
                      >
                        {DOC_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>

                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <i className="bi bi-sticky" />
                    Nota
                  </div>

                  <Form.Group>
                    <Form.Label>Nota</Form.Label>
                    <Form.Control
                      value={form.note}
                      onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                      placeholder="Opcional..."
                    />
                  </Form.Group>
                </div>

                <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                  <div className="cardlet-title">
                    <FiUpload />
                    Ficheiros
                  </div>

                  <Form.Group>
                    <Form.Label>
                      Selecionar ficheiros {editing ? "" : <span className="req">*</span>}
                    </Form.Label>
                    <Form.Control type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                    <div className="helper">
                      {files.length
                        ? `${files.length} ficheiro(s) selecionado(s).`
                        : editing
                        ? "Opcional: adicione ficheiros novos."
                        : "Selecione pelo menos 1 ficheiro."}
                    </div>

                    {files.length ? (
                      <div className="mt-2 file-list">
                        {files.map((f, idx) => (
                          <div className="file-item" key={`${f.name}-${idx}`}>
                            <div className="file-left">
                              <div className="btn-icon" style={{ cursor: "default", width: 34, height: 34 }} title={f.type || f.name}>
                                <i className={fileIconFor(f.type || f.name)} />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div className="file-name">{f.name}</div>
                                <div className="file-meta">
                                  {formatBytes(f.size)} {f.type ? `• ${f.type}` : ""}
                                </div>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm rounded-pill"
                              onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                              disabled={saving}
                            >
                              <FiX className="me-1" />
                              Remover
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </Form.Group>
                </div>
              </div>
            </Form>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 flex-wrap w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>

            <Button variant="primary" onClick={doUploadOrUpdate} disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  A guardar...
                </>
              ) : editing ? (
                "Atualizar"
              ) : (
                "Registar"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* DETAILS */}
      <Modal show={openDetails} onHide={closeDetails} centered backdrop="static" keyboard className="pretty-modal">
        <Modal.Header closeButton>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiEye style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>{safeValue(detailsRow?.title, "Detalhes do documento")}</div>
                <div className="title-sub">
                  {currentCategoryLabel(detailsRow?.category)} • {safeValue(detailsRow?.date)}
                </div>
              </div>
            </div>

            <span className="pill">
              <i className="bi bi-paperclip" />
              {detailsRow?.files?.length ?? detailsRow?.files_count ?? 0} ficheiro(s)
            </span>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            <div className="cardlet mb-3">
              <div className="cardlet-title">
                <i className="bi bi-info-circle" />
                Informação
              </div>

              <div className="form-grid">
                <div>
                  <div className="small-muted">Título</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(detailsRow?.title)}</div>
                </div>
                <div>
                  <div className="small-muted">Categoria</div>
                  <div style={{ fontWeight: 650 }}>{currentCategoryLabel(detailsRow?.category)}</div>
                </div>
                <div>
                  <div className="small-muted">Operador</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(detailsRow?.operator)}</div>
                </div>
                <div>
                  <div className="small-muted">Data</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(detailsRow?.date)}</div>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <div className="small-muted">Nota</div>
                  <div style={{ fontWeight: 650 }}>{safeValue(detailsRow?.note, "Sem nota")}</div>
                </div>
              </div>
            </div>

            <div className="cardlet">
              <div className="cardlet-title">
                <i className="bi bi-paperclip" />
                Ficheiros
              </div>

              <div className="file-list">
                {(detailsRow?.files || []).map((f, idx) => {
                  const url = resolveUrl(f.url || f.path || f.download_url);
                  const canPreview = isPreviewable(f);

                  return (
                    <div className="file-item" key={f.id || `${f.name}-${idx}`}>
                      <div className="file-left">
                        <div className="btn-icon" style={{ cursor: "default", width: 34, height: 34 }}>
                          <i className={fileIconFor(f.mime || f.name)} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div className="file-name">{safeValue(f.name, "Ficheiro")}</div>
                          <div className="file-meta">
                            {formatBytes(f.size)} {f.mime ? `• ${f.mime}` : ""} {url ? "" : " • (sem link)"}
                          </div>
                        </div>
                      </div>

                      <div className="file-actions">
                        {canPreview ? (
                          <button className="btn btn-outline-primary btn-sm rounded-pill" type="button" onClick={() => preview(f)} disabled={!url}>
                            <FiEye className="me-1" />
                            Preview
                          </button>
                        ) : null}

                        <button className="btn btn-outline-dark btn-sm rounded-pill" type="button" onClick={() => downloadFile(f)} disabled={!url}>
                          <FiDownload className="me-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  );
                })}

                {(detailsRow?.files || []).length === 0 ? (
                  <div className="file-item">
                    <div className="small-muted">Sem ficheiros (ou o endpoint não devolveu a lista).</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeDetails}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* PREVIEW */}
      <Modal show={showPreview} onHide={closePreview} centered backdrop="static" keyboard className="pretty-modal">
        <Modal.Header closeButton>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className="icon-wrap">
                <FiEye style={{ color: "#5b5cff", fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>Preview</div>
                <div className="title-sub">{safeValue(previewFile?.name)}</div>
              </div>
            </div>

            <button
              className="btn btn-outline-dark btn-sm rounded-pill d-flex align-items-center"
              onClick={() => previewFile?.url && window.open(previewFile.url, "_blank", "noopener,noreferrer")}
            >
              <FiDownload className="me-2" />
              Abrir
            </button>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {!previewFile?.url ? (
              <div className="alert alert-warning" style={{ borderRadius: 14 }}>
                Sem URL para preview.
              </div>
            ) : previewFile?.mime?.toLowerCase().includes("pdf") || (previewFile?.name || "").toLowerCase().endsWith(".pdf") ? (
              <div style={{ border: "1px solid rgba(226,232,240,.95)", borderRadius: 14, overflow: "hidden" }}>
                <iframe title="PDF Preview" src={previewFile.url} style={{ width: "100%", height: "70vh", border: "none" }} />
              </div>
            ) : (
              <div style={{ border: "1px solid rgba(226,232,240,.95)", borderRadius: 14, overflow: "hidden" }}>
                <img src={previewFile.url} alt="Preview" style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", background: "#fff" }} />
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closePreview}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* CONFIRM DELETE (pequeno) */}
      <Modal show={openConfirmDelete} onHide={closeConfirmDelete} centered backdrop="static" keyboard={!saving} className="confirm-modal">
        <Modal.Header closeButton={!saving}>
          <div className="confirm-title">
            <div className="confirm-icon">
              <FiAlertTriangle style={{ color: "#ef4444", fontSize: 18 }} />
            </div>
            <div>Confirmar remoção</div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="confirm-sub">
            Tens a certeza que queres remover o documento{" "}
            <span style={{ fontWeight: 750, color: "#0f172a" }}>
              “{safeValue(deleteTarget?.title, "Sem título")}”
            </span>
            ?
            <div style={{ marginTop: 8, fontSize: ".8rem", color: "#94a3b8" }}>
              Esta ação não pode ser desfeita.
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-end gap-2 w-100">
            <Button variant="outline-secondary" className="btn-soft" onClick={closeConfirmDelete} disabled={saving}>
              Cancelar
            </Button>

            <Button variant="danger" onClick={confirmDeleteDoc} disabled={saving || !deleteTarget?.id}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  A remover...
                </>
              ) : (
                "Remover"
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
